
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { BackendBaseUrl, useAuth } from "./global_varibles";
import HeaderAppBar from "./appbar";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Calendar, User } from "lucide-react";

// ---- Types ----
interface ManagerAsset {
  pick?: string;
  asset?: number;
}

interface SummaryData {
  todays_orders?: number;
  delivered_orders?: number;
  not_delivered_orders?: number;
  count_of_managers_asset: Record<string, ManagerAsset>;
}

interface GenSummaryProps {
  summary_data: SummaryData;
  selected_date: Dayjs;
}

interface OrdersQuery {
  date: string;
  is_summary?: boolean;
  status?: boolean;
  monthly_summ?: boolean;
}

// ---- Child Component ----
function GenarateSummaryData({ summary_data, selected_date }: GenSummaryProps) {
  const navigate = useNavigate();

  const ReRoutOrdersPage = async (data_to_send: OrdersQuery) => {
    try {
      const bkroute = `${BackendBaseUrl}search-bill`;
      const response = await fetch(bkroute, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data_to_send),
      });
      const response_data = await response.json();
      if (response_data.status === "success") {
        localStorage.setItem("all_orders", JSON.stringify(response_data.orders_date));
        navigate("/show-orders");
      } else {
        console.error("Error submitting data");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const DisplayTodaysOrders = () => {
    ReRoutOrdersPage({
      date: selected_date.toISOString(),
      is_summary: true,
    });
  };

  const DisplayMonthlyDeliveredOrders = () => {
    ReRoutOrdersPage({
      date: selected_date.toISOString(),
      is_summary: true,
      status: true,
      monthly_summ: true,
    });
  };

  const DisplayMonthlyNotDeliveredOrders = () => {
    ReRoutOrdersPage({
      date: selected_date.toISOString(),
      is_summary: true,
      status: false,
      monthly_summ: true,
    });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] px-4 sm:px-6 lg:px-8 py-8 relative">
      {/* Dot Pattern Background */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Today's Orders Card */}
        <div className="mb-12 flex justify-center">
          <div
            onClick={DisplayTodaysOrders}
            className="group w-full max-w-md cursor-pointer"
          >
            <div className="bg-white border border-orange-100 rounded-2xl p-8 transition-all duration-400 hover:-translate-y-2 shadow-xl shadow-orange-500/5 hover:shadow-orange-500/10 relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-orange-500"></div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center border border-orange-100">
                    <Calendar className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-gray-900 text-xl font-black">Today's Orders</h3>
                </div>
              </div>
              <div className="text-6xl font-black text-orange-600 mb-2 tracking-tighter">
                {summary_data.todays_orders ?? 0}
              </div>
              <p className="text-gray-500 font-bold text-sm uppercase tracking-wide">Click to view details</p>
            </div>
          </div>
        </div>

        {/* Monthly Summary Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-2">
              Monthly Summary
            </h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto rounded-full shadow-sm shadow-orange-500/20"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Delivered Orders Card */}
            <div
              onClick={DisplayMonthlyDeliveredOrders}
              className="group cursor-pointer"
            >
              <div className="bg-white border border-green-100 rounded-2xl p-8 transition-all duration-400 hover:-translate-y-2 shadow-lg shadow-green-500/5 hover:shadow-green-500/10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-green-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center border border-green-100">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="text-gray-900 text-xl font-bold">Delivered</h3>
                  </div>
                </div>
                <div className="text-5xl font-black text-green-600 mb-2">
                  {summary_data.delivered_orders ?? 0}
                </div>
                <p className="text-gray-500 font-medium text-sm">Successfully completed orders</p>
              </div>
            </div>

            {/* Not Delivered Orders Card */}
            <div
              onClick={DisplayMonthlyNotDeliveredOrders}
              className="group cursor-pointer"
            >
              <div className="bg-white border border-red-100 rounded-2xl p-8 transition-all duration-400 hover:-translate-y-2 shadow-lg shadow-red-500/5 hover:shadow-red-500/10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-red-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center border border-red-100">
                      <TrendingDown className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-gray-900 text-xl font-bold">Not Delivered</h3>
                  </div>
                </div>
                <div className="text-5xl font-black text-red-600 mb-2">
                  {summary_data.not_delivered_orders ?? 0}
                </div>
                <p className="text-gray-500 font-medium text-sm">Pending or cancelled orders</p>
              </div>
            </div>
          </div>
        </div>

        {/* Manager Assets Section */}
        {Object.keys(summary_data.count_of_managers_asset).length > 0 && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Manager Performance
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Object.entries(summary_data.count_of_managers_asset).map(([id, manager]) => (
                <div
                  key={id}
                  className="group"
                >
                  <div className="bg-gradient-to-br from-black/50 to-black/30 backdrop-blur-sm border border-white/10 hover:border-orange-500/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-orange-500/10">
                    <div className="flex flex-col items-center">
                      {/* Avatar */}
                      <div className="relative mb-4">
                        {manager.pick ? (
                          <img
                            src={manager.pick}
                            alt={id}
                            className="w-20 h-20 rounded-full object-cover border-4 border-orange-500/30 group-hover:border-orange-500 transition-all duration-300"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center border-4 border-orange-500/30 group-hover:border-orange-500 transition-all duration-300">
                            <User className="w-10 h-10 text-white" />
                          </div>
                        )}
                        <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-[#111827] flex items-center justify-center">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                      </div>

                      {/* Manager Name */}
                      <h3 className="text-white text-lg font-bold mb-2 text-center group-hover:text-orange-400 transition-colors duration-300">
                        {id}
                      </h3>

                      {/* Revenue */}
                      <div className="w-full bg-white/5 rounded-xl p-4 border border-white/10">
                        <p className="text-gray-400 text-xs mb-1 text-center">Total Revenue</p>
                        <p className="text-2xl font-bold text-white text-center">
                          ₹{manager.asset ?? 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Parent Page ----
function DisplaySummaryData() {
  const { isAuthinticated, user_data } = useAuth();
  const [summary_data, setSummmaryData] = useState<SummaryData>({
    count_of_managers_asset: {},
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

  useEffect(() => {
    if (isAuthinticated && user_data.id) {
      const GetSummaryData = async () => {
        try {
          setIsLoading(true);
          const data_to_get = {
            req_id: user_data.id,
            date: selectedDate.toISOString(),
          };
          const bkroute = `${BackendBaseUrl}orders-summary`;
          const response = await fetch(bkroute, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data_to_get),
          });
          const response_data = await response.json();
          if (response.ok && response_data.summary) {
            setSummmaryData({
              ...response_data.summary,
              count_of_managers_asset:
                response_data.summary.count_of_managers_asset ?? {},
            });
          } else {
            setSummmaryData({ count_of_managers_asset: {} });
          }
        } catch {
          setSummmaryData({ count_of_managers_asset: {} });
        } finally {
          setIsLoading(false);
        }
      };
      GetSummaryData();
    } else {
      setIsLoading(false);
    }
  }, [isAuthinticated, user_data.id, selectedDate]);

  if (!isAuthinticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f1218] to-[#111827] flex items-center justify-center">
        <div className="bg-black/30 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400">Please login to view this page</p>
        </div>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="min-h-screen bg-gradient-to-b from-[#0f1218] to-[#111827]">
        <HeaderAppBar />
        
        {/* Date Picker Section - IMPROVED VISIBILITY */}
        <div className="pt-8 pb-4 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-center">
              <div className="bg-gradient-to-br from-black/70 to-black/50 backdrop-blur-md border border-orange-500/30 rounded-2xl p-6 shadow-2xl shadow-orange-500/10">
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  format="MM/DD/YYYY"
                  onChange={(newDate) => setSelectedDate(newDate ?? selectedDate)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      InputProps: {
                        sx: {
                          color: '#fff',
                          fontWeight: 600,
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          // input text
                          '& input': {
                            color: '#fff',
                            WebkitTextFillColor: '#fff',
                            fontWeight: 700,
                          },
                          // placeholder (if shown)
                          '& input::placeholder': {
                            color: 'rgba(255,255,255,0.7)',
                            opacity: 1,
                          },
                          // border styling
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            borderWidth: '2px',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#f97316',
                            borderWidth: '2px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#f97316',
                            borderWidth: '2px',
                          },
                          // icon
                          '& .MuiSvgIcon-root': {
                            color: '#f97316',
                            fontSize: '1.8rem',
                          },
                        },
                      },
                      InputLabelProps: {
                        sx: {
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '1rem',
                          fontWeight: 600,
                          '&.Mui-focused': {
                            color: '#f97316',
                            fontWeight: 700,
                          },
                        },
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          backgroundColor: '#1f2937',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          '& .MuiPickersDay-root': {
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(249, 115, 22, 0.2)' },
                            '&.Mui-selected': {
                              backgroundColor: '#f97316 !important',
                              color: 'black',
                              fontWeight: 700,
                            },
                          },
                          '& .MuiPickersCalendarHeader-root': { color: 'white' },
                          '& .MuiPickersCalendarHeader-label': { color: 'white', fontWeight: 600 },
                          '& .MuiDayCalendar-weekDayLabel': { color: '#f97316', fontWeight: 600 },
                          '& .MuiPickersArrowSwitcher-button': { color: '#f97316' },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col justify-center items-center py-20 gap-4">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 bg-orange-500 rounded-full animate-pulse"></div>
              </div>
            </div>
            <p className="text-white text-lg font-semibold">Loading summary data...</p>
          </div>
        ) : (
          <GenarateSummaryData
            summary_data={summary_data}
            selected_date={selectedDate}
          />
        )}
      </div>
    </LocalizationProvider>
  );
}

export default DisplaySummaryData;


